import { useContext } from "react";
import styled, { ThemeContext } from "styled-components";
import _css from "@styled-system/css";

import LinkButton from "../Button/LinkButton";
import Popover from "../MenuPopover/MenuPopover";
import { useHover } from "../useHover.hook";
import { processEventLabel } from "../Navigation/navItem.functions";

import {
  NavItemProps,
  NavPopoverProps,
  NavPopoverWithSectionsProps,
  NavPopoverWithChildrenProps,
} from "./bcaHeader.type";

const Li = styled.li`
  position: relative;
  &:hover ul {
    visibility: visible;
  }
  &:focus-within ul {
    visibility: visible;
  }
  &:hover div {
    visibility: visible;
  }
  &:focus-within div {
    visibility: visible;
  }
  &:last-of-type {
    margin-right: 0;
  }
`;

const A = styled.a`
  line-height: 24px;
  ${_css({
    fontSize: "body",
    color: "secondary-darkest-grey",
    fontFamily: "normal",
    textDecoration: "none",
    "&:hover": {
      color: "primary-light-blue",
      textDecoration: "underline",
      cursor: "pointer",
    },
    "&:focus-within": {
      color: "primary-light-blue",
      textDecoration: "underline",
      cursor: "pointer",
    },
  })};
`;

const A2 = styled(A)`
  display: block;
  ${_css({
    "&:first-child": {
      color: "grey-blue",
      textTransform: "uppercase",
      fontSize: "label",
      fontWeight: "semiBold",
      paddingBottom: 1,
      textDecoration: "none",
      cursor: "auto",
    },
  })};
`;

const Ul = styled.ul`
  display: inline-flex;
  flex-direction: column;
  padding: 0 30px;
  position: relative;
  min-height: 163px;
  &:after {
    content: "";
    position: absolute;
    right: 0;
    width: 1px;
    height: 150px;
    background: #dedede;
    top: 6px;
  }
  &:first-of-type {
    padding-left: 0;
  }
  &:last-of-type {
    padding-right: 0;
    &:after {
      content: none;
    }
  }
`;

const NavPopover = ({
  children,
  as,
  css,
  role,
  index,
  sectionsCount = null,
}: NavPopoverProps) => (
  <Popover
    height="auto"
    width="auto"
    as={as}
    role={role}
    index={index}
    sectionsCount={sectionsCount}
    css={`
      position: absolute;
      top: calc(100% - 4px);
      left: 50%;
      transform: translateX(-50%);
      visibility: hidden;
      padding: 24px 28px;
      white-space: nowrap;
      ${css}
    `}
  >
    {children}
  </Popover>
);

const PopoverWithSections = ({
  sections,
  eventAction,
  eventSection,
  index,
}: NavPopoverWithSectionsProps) => {
  const translateX =
    index === 0 && sections.length > 1
      ? "transform: translateX(-20%);"
      : index === 1 && sections.length > 2
      ? "transform: translateX(-45%);"
      : "";
  return (
    <NavPopover
      as="div"
      role="submenu"
      css={`
        padding-top: 30px;
        ${translateX}
      `}
      aria-haspopup="true"
      index={index}
      sectionsCount={sections.length}
    >
      {sections.map(({ children, id }) => (
        <Ul key={id} role="menu" aria-haspopup="true">
          {children
            .filter((link) => link.isVisible)
            .map((link) => {
              const eventLabel = processEventLabel(link.label);

              return link.renderAsText ? (
                <A2 key={link.id} role="menuitem" as="span">
                  {link.label}
                </A2>
              ) : (
                <A2
                  key={link.id}
                  href={link.link}
                  role="menuitem"
                  target={link.shouldOpenInNewWindow ? "_blank" : "_top"}
                  data-en="click"
                  data-ec="button"
                  data-el={eventLabel}
                  data-ea={eventAction}
                  data-es={eventSection}
                >
                  {link.label}
                </A2>
              );
            })}
        </Ul>
      ))}
    </NavPopover>
  );
};

const PopoverWithChildren = ({
  children,
  eventAction,
  eventSection,
  index,
}: NavPopoverWithChildrenProps) => {
  return (
    <NavPopover
      as="ul"
      css={`
        display: flex;
        flex-direction: column;
      `}
      role="submenu"
      index={index}
    >
      {children
        .filter((link) => link.isVisible)
        .map((link) => {
          const eventLabel = processEventLabel(link.label);

          return (
            <A
              key={link.id}
              href={link.link}
              role="menuitem"
              target={link.shouldOpenInNewWindow ? "_blank" : "_top"}
              data-en="click"
              data-ec="button"
              data-el={eventLabel}
              data-ea={eventAction}
              data-es={eventSection}
            >
              {link.label}
            </A>
          );
        })}
    </NavPopover>
  );
};

const NavItem = ({ item, index }: NavItemProps) => {
  const themeContext = useContext(ThemeContext);
  const [hoverRef] = useHover<HTMLAnchorElement>();
  const { children, sections } = item;
  const eventLabel = processEventLabel(item.label);
  const eventAction = item.label === "Search" ? "search" : "navigate";

  const getInner = () => {
    if (children) {
      return (
        <PopoverWithChildren
          children={children}
          eventAction={eventAction}
          eventSection={eventLabel}
          index={index}
        />
      );
    }
    if (sections) {
      return (
        <PopoverWithSections
          sections={sections}
          eventAction={eventAction}
          eventSection={eventLabel}
          index={index}
        />
      );
    }
    return null;
  };

  const css = `
      position: relative;
      color: ${themeContext.colors["primary-white"]};
      &:not([disabled]):hover {
        color: ${themeContext.colors["primary-light-blue"]}
      }
      &:not([disabled]):focus {
        color: ${themeContext.colors["primary-light-blue"]}
      }`;
  return (
    <Li>
      <LinkButton
        variant="tertiary"
        css={css}
        p="0"
        link={item.link}
        ref={hoverRef}
        shouldOpenInNewWindow={item.shouldOpenInNewWindow}
        text={item.label}
        height="40px"
        aria-haspopup="true"
        analytics={{
          eventLabel,
          eventAction,
          eventSection: "header",
        }}
      ></LinkButton>
      {getInner()}
    </Li>
  );
};

export default NavItem;
